import {
    DefaultUserHomepageSelectionState,
    InitialUserHomepageState,
    UserHomepageComponent,
    UserHomepageComponentContent,
    UserHomepageComponentExpandability,
    UserHomepageComponentTypes,
    UserHomepageContent,
    UserHomepageDispatcherAction,
    UserHomepageExpandable,
    UserHomepageSortable,
    UserHomepageState,
    UserHomepageStateActions,
    UserHomePageStateData,
    UserHomepageTab,
} from "../models";
import {createUUId, deepCopy, numComparator} from "../../../../core/services/utils/utils";
import UserHomepageUtils from "./utils";

class UserHomepageReducer {

    /**
     * The logic that is to be passed into the React reducer.
     * @return {UserHomepageState}
     */
    get reducer() {
        return (state: any, action?: UserHomepageDispatcherAction): any => this._reducer(state, action);
    }

    /**
     * Fills the initialState object of the user homepage.
     *
     * @param  {InitialUserHomepageState} initialState
     * @return {UserHomepageState}
     */
    initializer(initialState: InitialUserHomepageState): Partial<UserHomepageState> {
        return {
            id: 0,
            currentData: initialState.data,
            updatedData: initialState.data,
            selection: DefaultUserHomepageSelectionState,
            viewMode: initialState.viewMode,
            controlPanel: {
                collapsed: initialState.controlPanelCollapsed,
            },
            initialState: initialState,
        };
    }

    /**
     * Sorts the given data of the homepage based on their orderIndex.
     *
     * @param {UserHomepageSortable} a
     * @param {UserHomepageSortable} b
     * @return {number}
     */
    comparator<T extends UserHomepageSortable>(a: T, b: T): number {
        return numComparator(a.orderIndex ?? 0, b.orderIndex ?? 0);
    }

    // ###########################       PREPARE DATA        ###########################

    /**
     * Creates the Element ids of the given homepage state data.
     * @param {any} state
     */
    prepareStateElement(state: Partial<UserHomepageState>): UserHomepageState {
        return ({
            ...state,
            controlPanelElementId: `control-panel-element-data-${state?.id}`,
            phoneViewElementId: `phone-view-element-data-${state?.id}`,
            webViewElementId: `web-view-element-data-${state?.id}`,
        }) as UserHomepageState;
    }

    /**
     * Creates the Element ids of the given homepage tab.
     * @param {Partial<UserHomepageState>} state
     * @param {Partial<UserHomepageTab>} tab
     * @return {UserHomepageTab}
     */
    prepareTabElement(state: UserHomepageState, tab: Partial<UserHomepageTab>): UserHomepageTab {
        return ({
            ...tab,
            controlPanelElementId: state.controlPanelElementId?.concat(`-tab-${tab.id}`),
            phoneViewElementId: state.phoneViewElementId?.concat(`-tab-${tab.id}`),
            webViewElementId: state.webViewElementId?.concat(`-tab-${tab.id}`),
        }) as UserHomepageTab;
    }

    /**
     * Creates the Element ids of the given homepage component.
     * @param {UserHomepageTab} tab
     * @param {Partial<UserHomepageTab>} component
     * @return {UserHomepageComponent}
     */
    prepareComponentElement(tab: UserHomepageTab, component: Partial<UserHomepageComponent>): UserHomepageComponent {
        return ({
            ...component,
            controlPanelElementId: tab.controlPanelElementId?.concat(`-component-${component.id}`),
            phoneViewElementId: tab.phoneViewElementId?.concat(`-component-${component.id}`),
            webViewElementId: tab.webViewElementId?.concat(`-component-${component.id}`),
        }) as UserHomepageComponent;
    }

    /**
     * Creates the Element ids of the given homepage content.
     * @param {Partial<UserHomepageTab>} component
     * @param {Partial<UserHomepageContent>} content
     * @return {UserHomepageContent}
     */
    prepareContentElement(component: UserHomepageComponent, content: Partial<UserHomepageContent>): UserHomepageContent {
        return ({
            ...content,
            controlPanelElementId: component.controlPanelElementId?.concat(`-content-${component.id}`),
            phoneViewElementId: component.phoneViewElementId?.concat(`-content-${component.id}`),
            webViewElementId: component.webViewElementId?.concat(`-content-${component.id}`),
        }) as UserHomepageContent;
    }


    /**
     * Prepares a components' search query object.
     *
     * * transforms the keys of any objects in the search query by ensuring they are:
     *  1. camel case
     *  2. ID's are turned into Id's
     * @param entity
     */
    prepareComponentSearchQuery(entity?: any): any {
        if (typeof entity === "undefined" || entity === null)
            return null;

        if (typeof entity === 'number' || typeof entity === 'string')
            return entity;

        if (Array.isArray(entity))
            return entity.map(entity => this.prepareComponentSearchQuery(entity));

        if (typeof entity === 'object') {
            // @ts-ignore
            return Object.fromEntries(Object.entries(entity).map(([key, value]) => {
                let _key;
                if (key.toLowerCase().trim() === 'id') {
                    _key = 'id'
                } else {
                    _key = key.substring(0, 1).toLowerCase().concat(key.substring(1).replace('ID', 'Id'))
                }
                return [
                    _key,
                    this.prepareComponentSearchQuery(value),
                ]
            }))
        }
    }

    /**
     * Prepares the tabs of the homepage view.
     * @param {UserHomepageState} state
     * @param data
     * @param config
     * @param internal
     * @return {UserHomePageStateData | undefined}
     */
    prepareData(state: UserHomepageState, data: any, internal = false, config: any): UserHomePageStateData {
        return {
            ...data,
            canEdit: data?.isEditable ?? false,
            title: data?.title,
            tabs: data?.tabs
                ?.map((tab: any) => this.prepareTab(state, tab, internal, config))
                ?.sort(this.comparator)
                ?.map((tab: any, index: number) => {
                    if (index === 0) {
                        return {...tab, loading: false};
                    }
                    return {...tab, loading: true};
                })
        }
    }

    /**
     * Prepares the given tab data by injecting the extra information.
     *
     * @param state
     * @param tab
     * @param config
     * @param internal whether this method is being used internally, and thus config will have no effect in construction
     * of this tab
     */
    prepareTab(state: UserHomepageState, tab: any, internal = false, config?: any,): UserHomepageTab {
        const loading = !tab?.components;
        tab = this.prepareTabElement(state, {
            ...tab,
            expanded: tab?.expanded ?? false,
            loading: typeof tab?.loading === 'boolean' ? tab?.loading : loading,
            components: tab?.components?.sort(this.comparator),
        })
        return {
            ...tab,
            components: tab?.components?.map((component: any) => this.prepareComponent(tab, component, internal, config)),
        }
    }

    /**
     * Prepares the given component by injecting the necessary information into its object.
     *
     * * filters out components that have valid entries for their content
     * * for each component, inject the given configs in their properties.
     *
     * @param tab
     * @param component
     * @param config
     * @param internal whether this method is being used internally, and thus config will have no effect in construction
     * of this component
     */
    prepareComponent(tab: UserHomepageTab, component: UserHomepageComponent, internal = false, config?: any): UserHomepageComponent {
        component = this.prepareComponentElement(tab, component)
        const content = this.prepareComponentContent(component, internal, config);
        const searchQuery = component.searchQuery
            // @ts-ignore
            ? this.prepareComponentSearchQuery(JSON.parse((component.searchQuery as string) ?? "{}"))
            : null;
        return {
            ...component,
            expanded: component?.expanded ?? UserHomepageComponentExpandability.none,
            endDate: component?.content?.finishDateTime ?? component?.endDate,
            content: content,
            searchQuery: searchQuery,
        }
    }

    /**
     * Prepares the content of the component.
     *
     * * for each component, inject the given configs in their properties.
     * @param component
     * @param internal
     * @param config
     */
    prepareComponentContent(component: UserHomepageComponent, internal = false, config?: any): UserHomepageComponentContent {
        let componentContent: UserHomepageComponentContent = component.content ?? {};
        switch (component?.type?.id) {
            case UserHomepageComponentTypes.slideShow:
            case UserHomepageComponentTypes.banner: {
                const banners = internal
                    ? componentContent?.banners ?? []
                    : componentContent?.banners?.map((e) => ({
                    ...e,
                    imageName: !!e?.imageName ? config?.BannerUrlBaseAddress?.concat(e?.imageName) : null,
                    tags: e?.tags?.map((e: any) => ({
                        ...e,
                        iconFileName: !!e?.iconFileName ? config?.TagIconUrlBaseAddress?.concat(e?.iconFileName) : null
                    })) ?? [],
                })) ?? []
                componentContent = {
                    ...componentContent,
                    banners: banners,
                }
                break;
            }
            case UserHomepageComponentTypes.imageCarousel: {
                const images = internal
                    ? componentContent?.images ?? []
                    : componentContent?.images?.map((e) => ({
                    ...e,
                    tags: e?.tags?.map((e: any) => ({
                        ...e,
                        iconFileName: !!e?.iconFileName ? config?.TagIconUrlBaseAddress?.concat(e?.iconFileName) : null
                    })) ?? [],
                })) ?? []
                componentContent = {
                    ...componentContent,
                    images: images,
                }
                break;
            }
            case UserHomepageComponentTypes.category: {
                const categories = internal
                    ? componentContent?.categories ?? []
                    : componentContent?.categories?.map((e) => ({
                    ...e,
                    coverFileName: !!e?.coverFileName ? config?.CategoryImageURL?.concat(e?.coverFileName) : null,
                })) ?? []
                componentContent = {
                    ...componentContent,
                    categories: categories,
                }
                break;
            }
            case UserHomepageComponentTypes.largeProduct:
            case UserHomepageComponentTypes.mediumProduct:
            case UserHomepageComponentTypes.smallProduct:
            case UserHomepageComponentTypes.timeBased: {
                const parts = internal
                    ? componentContent?.parts ?? []
                    : componentContent?.parts?.map((e) => ({
                    ...e,
                    category: {
                        ...(e?.category ?? {}),
                        coverFileName: !!e?.category?.coverFileName ? config?.CategoryImageURL?.concat(e?.category?.coverFileName) : null,
                    },
                    tags: e?.tags?.map((e: any) => ({
                        ...e,
                        iconFileName: !!e?.iconFileName ? config?.TagIconUrlBaseAddress?.concat(e?.iconFileName) : null
                    })) ?? [],
                    coverImageURL: !!e?.coverImageURL ? config?.PartImageURL?.concat(e?.coverImageURL) : null,
                })) ?? []
                componentContent = {
                    ...componentContent,
                    parts: parts,
                }
                break;
            }
            default:
                break;
        }
        return {
            ...componentContent,
            parts: componentContent?.parts
                ?.sort(this.comparator)
                ?.map((e) => this.prepareContent(component, e)),
            categories: componentContent?.categories
                ?.sort(this.comparator)
                ?.map((e) => this.prepareContent(component, e)),
            banners: componentContent?.banners
                ?.sort(this.comparator)
                ?.map((e) => this.prepareContent(component, e)),
            images: componentContent?.images
                ?.sort(this.comparator)
                ?.map((e) => this.prepareContent(component, e)),
            imageWidth: componentContent?.imageWidth ?? null,
            imageHeight: componentContent?.imageHeight ?? null,
            finishDateTime: componentContent?.finishDateTime,
        }
    }

    /**
     * Prepares a single content entity of a component content object.
     * @param {UserHomepageComponent} component
     * @param {Partial<UserHomepageContent>} content
     * @return {UserHomepageContent}
     */
    prepareContent(component: UserHomepageComponent, content: Partial<UserHomepageContent>): UserHomepageContent {
        content = this.prepareContentElement(component, content);
        return {
            ...content,
        } as UserHomepageContent;
    }

    // ###########################       REDUCER MUTATORS       ###########################

    /**
     * Updates the title of the state.
     * @param {UserHomePageStateData} data
     * @param {title} title
     * @param {boolean} commit
     */
    updateTitleMutator(data?: UserHomePageStateData, title?: string, commit = false): any {
        if (!commit) {
            return data;
        }
        return ({
            ...(data ?? {}),
            name: title,
        })
    }

    /**
     * Injects the given tab to the given data object.
     *
     * * since the tab is loaded, its loading property will also be set to false.
     * @param tab
     * @param data
     */
    tabInfoLoadedMutator(tab: UserHomepageTab, data?: UserHomePageStateData): any {
        return ({
            ...(data ?? {}),
            tabs: data?.tabs?.map<UserHomepageTab>((e) => e.id === tab.id
                ? {
                    ...e,
                    ...tab,
                    expanded: e.expanded,
                    loading: false,
                }
                : e
            ),
        })
    }

    /**
     * Injects the given tabs list. into the given data object.
     * @param data
     * @param tabs
     */
    reorderTabsMutator(data?: UserHomePageStateData, tabs?: UserHomepageTab[]): any {
        return ({
            ...(data ?? {}),
            tabs: tabs,
        })
    }

    /**
     * Injects the newly created tab to the tabs of the data.
     * @param state
     * @param newTab
     * @param data
     */
    createTabMutator(state: UserHomepageState, newTab: UserHomepageTab, data?: UserHomePageStateData): any {
        return ({
            ...(data ?? {}),
            tabs: [
                ...(data?.tabs ?? []),
                {
                    ...this.prepareTab(state, newTab, true),
                    loading: false,
                },
            ].sort(this.comparator)
        })
    }

    /**
     * Opens the tab associated with the given tabId of the data.
     *
     * * closes all the components in the data as well.
     * @param data
     * @param tabId
     */
    openTabMutator(data?: UserHomePageStateData, tabId?: number): any {
        return ({
            ...(data ?? {}),
            tabs: data?.tabs?.map((e) => ({
                ...e,
                expanded: e.id === tabId,
                components: e.components?.map((e) => ({
                    ...e,
                    expanded: UserHomepageComponentExpandability.none,
                })) ?? []
            })) ?? []
        })
    }

    /**
     * Updates the tab information associated with the given tab.
     *
     * * if not committing returns the same data.
     * @param {Partial<UserHomepageTab>} tab
     * @param {UserHomePageStateData} data
     * @param {boolean} commit
     */
    updateTabMutator(tab: Partial<UserHomepageTab>, data?: UserHomePageStateData, commit = true): any {
        if (!commit) {
            return data;
        }
        return {
            ...(data ?? {}),
            tabs: data?.tabs?.map((e) => e.id === tab.id
                ? {
                    ...e,
                    ...tab,
                }
                : e
            ) ?? [],
        }
    }

    /**
     * Removes the tab associated with the tabId of the data.
     * @param {UserHomePageStateData} data
     * @param {number} tabId
     */
    removeTabMutator(data?: UserHomePageStateData, tabId?: number): any {
        return ({
            ...(data ?? {}),
            tabs: data?.tabs?.filter((e) => e.id !== tabId) ?? [],
        })
    }

    /**
     * Reorders the components associated with the tabId of the data.
     * @param {UserHomePageStateData} data
     * @param {number} tabId
     * @param {UserHomepageComponent[]} components
     */
    reorderComponentsMutator(data?: UserHomePageStateData, tabId?: number, components?: UserHomepageComponent[]): any {
        return ({
            ...(data ?? {}),
            tabs: data?.tabs?.map((e) => e.id === tabId
                    ? {
                        ...e,
                        components: components
                    }
                    : e
                )
                ?? []
        })
    }

    /**
     * Adds the given component to the component list of the tab associated with the given tabId.
     * @param {UserHomepageComponent} component
     * @param {UserHomePageStateData} data
     * @param {number} tabId
     */
    createComponentMutator(component: UserHomepageComponent, data?: UserHomePageStateData, tabId?: number): any {
        return ({
            ...(data ?? {}),
            tabs: data?.tabs?.map((e) =>
                e.id === tabId
                    ? {
                        ...e,
                        components: [
                            ...(e.components ?? []),
                            component,
                        ].sort(this.comparator)
                    }
                    : e)
        })
    }

    /**
     * Updates the component associated with the tabId with the given partial component information.
     * @param {UserHomePageStateData} data
     * @param {number} tabId
     * @param {Partial<UserHomepageComponent>} component
     * @param {boolean} commit
     */
    updateComponentMutator(data?: UserHomePageStateData, tabId?: number, component?: Partial<UserHomepageComponent>, commit = true): any {
        if (!commit) {
            return data;
        }
        return {
            ...(data ?? {}),
            tabs: data?.tabs?.map((e) => e.id === tabId
                ? {
                    ...e,
                    components: e.components?.map<UserHomepageComponent>((e) => e.id === component?.id
                        ? (() => {
                            const hasEndDate = component != null && Object.prototype.hasOwnProperty.call(component, 'endDate');
                            const hasFinishDateTime = component?.content != null
                                && Object.prototype.hasOwnProperty.call(component.content, 'finishDateTime');
                            const finishDateTime = hasFinishDateTime
                                ? component.content?.finishDateTime
                                : hasEndDate
                                    ? component.endDate
                                    : (e.content?.finishDateTime ?? e.endDate);
                            const endDate = hasEndDate
                                ? component.endDate
                                : hasFinishDateTime
                                    ? component.content?.finishDateTime
                                    : (e.content?.finishDateTime ?? e.endDate);

                            return {
                                ...e,
                                ...component,
                                endDate,
                                content: {
                                    ...e.content,
                                    ...(component?.content ?? {}),
                                    finishDateTime,
                                },
                            };
                        })()
                        : e) ?? [],
                }
                : e) ?? [],
        }
    }

    /**
     * Removes the component associated with the componentId from the list of components associated with the given
     * tabId.
     * @param {UserHomePageStateData} data
     * @param {number} tabId
     * @param {number} componentId
     */
    removeComponentMutator(data?: UserHomePageStateData, tabId?: number, componentId?: number): any {
        return ({
            ...(data ?? {}),
            tabs: data?.tabs?.map((e) => e.id === tabId
                ? {
                    ...e,
                    components: e.components?.filter((e) => e.id !== componentId) ?? [],
                }
                : e
            )
        })
    }

    /**
     * Opens the component associated with the componentId from the list of components associated with the given
     * tabId.
     *
     * * opens the tab that the component belongs in
     * * closes all the other components and other tabs in the data.
     * @param {UserHomePageStateData} data
     * @param {number} tabId
     * @param {number} componentId
     * @param {UserHomepageExpandable} expanded
     */
    openComponentMutator(data?: UserHomePageStateData, tabId?: number, componentId?: number, expanded?: UserHomepageExpandable): any {
        return ({
            ...(data ?? {}),
            tabs: data?.tabs?.map((e) => e.id === tabId
                ? {
                    ...e,
                    expanded: true,
                    components: e.components?.map((e) => ({
                        ...e,
                        expanded: e.id === componentId
                            ? expanded
                            : UserHomepageComponentExpandability.none,
                    }))
                }
                : {
                    ...e,
                    expanded: false,
                }
            ) ?? []
        })
    }

    /**
     * Reorders the content associated with the componentId and tabId.
     * @param {UserHomePageStateData} data
     * @param {number} tabId
     * @param {number} componentId
     * @param {UserHomepageContent[]} contentList
     */
    reorderContentMutator(data?: UserHomePageStateData, tabId?: number, componentId?: number, contentList?: UserHomepageContent[]): any {
        return ({
            ...(data ?? {}),
            tabs: data?.tabs?.map((e) => e.id === tabId
                    ? {
                        ...e,
                        components: e.components?.map(e => e.id === componentId
                            ? UserHomepageUtils.updateComponentContentList(e, contentList)
                            : e
                        ) ?? []
                    }
                    : e
                )
                ?? []
        })
    }

    /**
     * Adds the given content to the list of content associated with the given componentId and tabId.
     * @param {UserHomePageStateData} data
     * @param {number} tabId
     * @param {number} componentId
     * @param {UserHomepageContent} content
     * @param {boolean} commit
     */
    addContentMutator(data?: UserHomePageStateData, tabId?: number, componentId?: number, content?: UserHomepageContent, commit = true): any {
        if (!commit) {
            return data;
        }
        return ({
            ...(data ?? {}),
            tabs: data?.tabs?.map((e) => e.id === tabId
                    ? {
                        ...e,
                        components: e.components?.map(e => e.id === componentId
                            ? UserHomepageUtils.appendContentToComponentContentList(e, this.prepareContent(e, content as UserHomepageContent))
                            : e
                        ) ?? []
                    }
                    : e
                )
                ?? []
        })
    }

    /**
     * removes the content associated with the given contentId belonging to the component and tab associated with
     * the given componentId and tabId.
     * @param {UserHomePageStateData} data
     * @param {number} tabId
     * @param {number} componentId
     * @param {number} contentId
     * @param {boolean} commit
     */
    removeContentMutator(data?: UserHomePageStateData, tabId?: number, componentId?: number, contentId?: number, commit = true): any {
        if (!commit) {
            return data;
        }
        return ({
            ...(data ?? {}),
            tabs: data?.tabs?.map((e) => e.id === tabId
                    ? {
                        ...e,
                        components: e.components?.map(e => e.id === componentId
                            ? UserHomepageUtils.removeContentFromComponentContentList(e, contentId)
                            : e
                        ) ?? []
                    }
                    : e
                )
                ?? []
        })
    }

    /**
     * Updates the content associated with the given content information belonging to the component and tab associated
     * with the given componentId and tabId.
     * @param data
     * @param tabId
     * @param componentId
     * @param content
     * @param {boolean} commit
     */
    updateContentMutator(data?: UserHomePageStateData, tabId?: number, componentId?: number, content?: UserHomepageContent, commit = true): any {
        if (!commit) {
            return data;
        }
        return ({
            ...(data ?? {}),
            tabs: data?.tabs?.map((e) => e.id === tabId
                    ? {
                        ...e,
                        components: e.components?.map(e => e.id === componentId
                            ? UserHomepageUtils.updateContentInComponentContentList(e, content)
                            : e
                        ) ?? []
                    }
                    : e
                )
                ?? []
        })
    }

    // ###########################       REDUCER       ###########################


    /**
     * Performs the logic of this reducer, and returns the new state.
     *
     * @param {UserHomepageState} state
     * @param {UserHomepageDispatcherAction} action
     * @return {UserHomepageState}
     * @private
     */
    private _reducer(state: UserHomepageState, action?: UserHomepageDispatcherAction): UserHomepageState {
        action = (
            action ?? {
                type: UserHomepageStateActions.none,
                payload: null,
            }
        ) as UserHomepageDispatcherAction;

        switch (action?.type) {

            // ############################         STATE         #############################

            case UserHomepageStateActions.resetState: {
                return this.initializer(state.initialState) as UserHomepageState;
            }

            case UserHomepageStateActions.setAsLive: {
                return {
                    ...state,
                    currentData: {
                        ...(state?.currentData ?? {}),
                        isLive: true
                    },
                    updatedData: {
                        ...(state?.updatedData ?? {}),
                        isLive: true
                    },
                };
            }


            case UserHomepageStateActions.changeViewMode: {
                return {
                    ...state,
                    viewMode: action.payload,
                }
            }

            case UserHomepageStateActions.updateTitle: {
                return {
                    ...state,
                    currentData: this.updateTitleMutator(state.currentData, action.payload.title, action.payload.commit),
                    updatedData: this.updateTitleMutator(state.updatedData, action.payload.title),
                }
            }

            // ############################         CONTROL PANEL         #############################

            case UserHomepageStateActions.toggleControlPanelCollapsed: {
                return {
                    ...state,
                    controlPanel: {
                        ...state.controlPanel,
                        collapsed: action.payload,
                    },
                }
            }

            // ############################         LOADING DATA         #############################

            case UserHomepageStateActions.homepageLoaded: {
                const id = action?.payload.data?.id ?? createUUId(true);
                state = this.prepareStateElement({
                    ...state,
                    id: id,
                });
                const currentData = this.prepareData(state, action?.payload.data, false, action?.payload.config);
                return {
                    ...state,
                    currentData: currentData,
                    updatedData: deepCopy(currentData),
                }
            }

            case UserHomepageStateActions.tabInfoLoaded: {
                const tab = this.prepareTab(state, action?.payload.tab, false, action?.payload?.config);
                return {
                    ...state,
                    currentData: this.tabInfoLoadedMutator(tab, state.currentData),
                    updatedData: this.tabInfoLoadedMutator(tab, state.updatedData)
                }
            }

            // ############################         SELECTING DATA         #############################

            case UserHomepageStateActions.selectTab: {
                return {
                    ...state,
                    selection: {
                        ...DefaultUserHomepageSelectionState, // selecting a new tab removes other selections
                        tabId: action?.payload.tabId,
                    },
                }
            }

            case UserHomepageStateActions.selectComponent: {
                return {
                    ...state,
                    selection: {
                        ...DefaultUserHomepageSelectionState, // selecting a new component removes other selections
                        tabId: action?.payload.tabId,
                        componentId: action?.payload.componentId,
                    },
                }
            }

            case UserHomepageStateActions.selectContent: {
                return {
                    ...state,
                    selection: {
                        ...DefaultUserHomepageSelectionState, // selecting a new component removes other selections
                        tabId: action?.payload.tabId,
                        componentId: action?.payload.componentId,
                        contentId: action?.payload.contentId,
                    },
                }
            }

            // ############################        TAB CRUD         #############################

            case UserHomepageStateActions.reorderTabs: {
                // the given order in the list should be the correct order, so set the orderIndices based on that
                const updatedTabs = (action?.payload.tabs as UserHomepageTab[])?.map((e, i) => ({
                    ...e,
                    orderIndex: i + 1,
                })) ?? [];
                // inject the orderIndex of updatedTabs into currentIndex based on their id to sort these as well but
                // preserve any other properties
                const currentTabs = state?.currentData?.tabs
                        ?.map(e => ({
                            ...e,
                            orderIndex: updatedTabs.find((t) => e.id === t.id)?.orderIndex ?? 0,
                        }))
                        ?.sort(this.comparator)
                    ?? []
                return {
                    ...state,
                    currentData: this.reorderTabsMutator(state.currentData, currentTabs),
                    updatedData: this.reorderTabsMutator(state.updatedData, updatedTabs),
                };
            }

            case UserHomepageStateActions.createTab: {
                const newTab = this.prepareTab(state, action?.payload, true);
                return {
                    ...state,
                    currentData: this.createTabMutator(state, newTab, state.currentData),
                    updatedData: this.createTabMutator(state, newTab, state.updatedData),
                }
            }

            case UserHomepageStateActions.openTab: {
                return {
                    ...state,
                    currentData: this.openTabMutator(state.currentData, action.payload.id),
                    updatedData: this.openTabMutator(state.updatedData, action.payload.id),
                };
            }

            case UserHomepageStateActions.updateTab: {
                return {
                    ...state,
                    currentData: this.updateTabMutator(action.payload.tab, state.currentData, action?.payload.commit),
                    updatedData: this.updateTabMutator(action.payload.tab, state.updatedData),
                }
            }

            case UserHomepageStateActions.removeTab: {
                return {
                    ...state,
                    currentData: this.removeTabMutator(state.currentData, action?.payload.id),
                    updatedData: this.removeTabMutator(state.updatedData, action?.payload.id),
                }
            }

            // ############################        COMPONENT CRUD         #############################

            case UserHomepageStateActions.reorderComponents: {
                // the given order in the list should be the correct order, so set the orderIndices based on that
                const updatedComponents = (action?.payload.components as UserHomepageComponent[])?.map((e, i) => ({
                    ...e,
                    orderIndex: i + 1,
                })) ?? [];
                // inject the orderIndex of updatedComponents into currentIndex based on their id to sort these as well but
                // preserve any other properties
                const currentComponents = state?.currentData?.tabs
                        ?.find(e => e.id === action?.payload.tabId)?.components
                        ?.map((e) => ({
                            ...e,
                            orderIndex: updatedComponents.find((c) => e.id === c.id)?.orderIndex ?? 0,
                        }))
                        ?.sort(this.comparator)
                    ?? []
                return {
                    ...state,
                    currentData: this.reorderComponentsMutator(state.currentData, action.payload.tabId, currentComponents),
                    updatedData: this.reorderComponentsMutator(state.updatedData, action.payload.tabId, updatedComponents),
                }
            }

            case UserHomepageStateActions.createComponent: {
                const tab = state?.updatedData?.tabs?.find(e => e.id === action?.payload.tabId);
                if (!tab)
                    return state;
                const newComponent = this.prepareComponent(tab, action?.payload.data);
                return {
                    ...state,
                    currentData: this.createComponentMutator(newComponent, state.currentData, tab.id),
                    updatedData: this.createComponentMutator(newComponent, state.updatedData, tab.id),
                }
            }

            case UserHomepageStateActions.updateComponent: {
                return {
                    ...state,
                    currentData: this.updateComponentMutator(state.currentData, action?.payload?.tabId, action.payload?.component, action?.payload?.commit),
                    updatedData: this.updateComponentMutator(state.updatedData, action?.payload?.tabId, action.payload?.component),
                }
            }

            case UserHomepageStateActions.removeComponent: {
                return {
                    ...state,
                    currentData: this.removeComponentMutator(state.currentData, action.payload?.tabId, action?.payload?.id),
                    updatedData: this.removeComponentMutator(state.updatedData, action.payload?.tabId, action?.payload?.id),

                }
            }

            case UserHomepageStateActions.openComponent: {
                return {
                    ...state,
                    currentData: this.openComponentMutator(state.currentData, action?.payload.tabId, action?.payload.id, action?.payload.mode),
                    updatedData: this.openComponentMutator(state.updatedData, action?.payload.tabId, action?.payload.id, action?.payload.mode),
                }
            }

            // ############################        CONTENT CRUD         #############################

            case UserHomepageStateActions.reorderContent: {
                // the given order in the list should be the correct order, so set the orderIndices based on that
                const updatedContentList = (action?.payload.content as UserHomepageContent[])?.map((e, i) => ({
                    ...e,
                    orderIndex: i + 1,
                })) ?? [];

                const currentComponent = state?.currentData?.tabs
                    ?.find(e => e.id === action?.payload.tabId)?.components
                    ?.find(e => e.id === action?.payload.componentId);

                // TODO: inject the orderIndex of updatedContentList into currentIndex based on their id to sort these as well but
                //  preserve any other properties
                const currentContentList = UserHomepageUtils.getComponentContentList(currentComponent)
                    .map((e) => ({
                        ...e,
                        orderIndex: updatedContentList.find((c) => e.id === c.id)?.orderIndex ?? 0,
                    }))
                    .sort(this.comparator)

                return {
                    ...state,
                    currentData: state.currentData,
                    updatedData: this.reorderContentMutator(state.currentData, action?.payload?.tabId, action?.payload?.componentId, updatedContentList),
                }
            }

            case UserHomepageStateActions.addContent: {
                return {
                    ...state,
                    currentData: this.addContentMutator(state.currentData, action?.payload.tabId, action?.payload.componentId, action?.payload.content, action?.payload.commit),
                    updatedData: this.addContentMutator(state.updatedData, action?.payload.tabId, action?.payload.componentId, action?.payload.content),
                }
            }

            case UserHomepageStateActions.removeContent:
                return {
                    ...state,
                    currentData: this.removeContentMutator(state.currentData, action?.payload.tabId, action?.payload.componentId, action?.payload.contentId, action?.payload.commit),
                    updatedData: this.removeContentMutator(state.updatedData, action?.payload.tabId, action?.payload.componentId, action?.payload.contentId),
                }

            case UserHomepageStateActions.updateContent:
                return {
                    ...state,
                    currentData: this.updateContentMutator(state.currentData, action?.payload.tabId, action?.payload.componentId, action?.payload.content, action?.payload.commit),
                    updatedData: this.updateContentMutator(state.updatedData, action?.payload.tabId, action?.payload.componentId, action?.payload.content),
                }


            default:
                return state;
        }
    }

}


export default UserHomepageReducer;
